import React, { FormEvent, useEffect, useState } from "react";
import {
  Button,
  Form,
  SpaceBetween,
} from "@amzn/awsui-components-react-v3/polaris";
import { Alerts } from "../components/alerts";
import { Alerts as AlertsType } from "../lib/types";
import { FormField, Input } from "@amzn/awsui-components-react-v3";
import {
  sanitizeNickname,
  isValidModhex,
  MAX_NICKNAME_LENGTH,
} from "../utils/input-validation";

interface RegisterMfaFormProps {
  error: string;
  onSubmit: (otp: string, nickname: string) => void;
  isChromium: boolean;
  isSuccess: boolean;
}

interface RegisterOtpKeyFormState {
  otp: string;
  nickname: string;
  hasSubmittedAtLeastOnce: boolean;
}

const initialFormState: RegisterOtpKeyFormState = {
  otp: "",
  nickname: "",
  hasSubmittedAtLeastOnce: false,
};

export const RegisterMfaForm = ({
  error,
  onSubmit,
  isSuccess,
  isChromium,
}: RegisterMfaFormProps) => {
  const [formAlerts, setFormAlerts] = useState<AlertsType>({});
  const [formState, setFormState] = useState(initialFormState);
  const [authInProgress, setAuthInProgress] = useState(false);

  let otpError = "";
  if (formState.otp.length === 0) {
    otpError = "OTP is required";
  } else if (formState.otp.length !== 44) {
    otpError = "Invalid OTP length.";
  } else if (!isValidModhex(formState.otp.toLowerCase())) {
    otpError =
      "Invalid OTP format, please switch to a standard keyboard layout to register your key.";
  }

  // clear the form and re-enable fields after response
  useEffect(() => {
    if (isSuccess || error) {
      setFormState(initialFormState);
      setAuthInProgress(false);
    }
  }, [isSuccess, error]);

  useEffect(() => {
    if (!isChromium) {
      setFormAlerts((prevAlerts) => ({
        ...prevAlerts,
        browser_compatability_warning: {
          message:
            "There are known issues with registering FIDO2 keys in this browser. Please use Chrome or Edge to complete registration.",
          ariaLabel: "Warning",
          type: "warning",
        },
      }));
    }
  }, [isChromium]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!otpError) {
      onSubmit(formState.otp.toLowerCase(), formState.nickname.trim());
      setAuthInProgress(true);
    }
    setFormState((prev) => ({ ...prev, hasSubmittedAtLeastOnce: true }));
  };

  // the form should be disabled after the user has submitted the OTP and is actively completing registration; also
  // if the user has successfully completed the registration process, disable the form so they know they are done
  const formDisabled = authInProgress || isSuccess;

  return (
    <SpaceBetween direction="vertical" size="s">
      <Alerts items={formAlerts} />
      {isChromium && (
        <form onSubmit={handleSubmit}>
          <Form
            actions={
              <Button
                disabled={formDisabled}
                variant="primary"
                data-testid="register-button"
              >
                Register
              </Button>
            }
            errorText={error}
            data-testid="register-mfa-form"
          >
            <SpaceBetween direction="vertical" size="s">
              <p data-testid="form-info">
                You are registering a new FIDO2 security key credential. If
                recognized, your key will also be registered for OTP use with
                Frontier. After submitting this form, please follow the pop-up
                window prompts to complete the registration process.
              </p>
              <FormField
                label={
                  <span>
                    Nickname <i>- optional</i>{" "}
                  </span>
                }
                description="Add a friendly nickname to help easily identity your credentials later."
                warningText={
                  formState.nickname.length >= MAX_NICKNAME_LENGTH
                    ? `Maximum nickname length reached (${MAX_NICKNAME_LENGTH} characters).`
                    : ""
                }
                data-testid="mfa-nickname-form-field"
              >
                <Input
                  disabled={formDisabled}
                  value={formState.nickname}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      nickname: sanitizeNickname(e.detail.value),
                    }))
                  }
                  data-testid="mfa-nickname-input"
                  autoComplete={false}
                />
              </FormField>
              <FormField
                label="OTP"
                errorText={formState.hasSubmittedAtLeastOnce ? otpError : ""}
                description="Press and hold your key for 3-5 seconds to generate a OTP."
                data-testid="mfa-otp-form-field"
              >
                <Input
                  disabled={formDisabled}
                  type="password"
                  value={formState.otp}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      otp: e.detail.value.trim(),
                    }))
                  }
                  data-testid="mfa-otp-input"
                />
              </FormField>
            </SpaceBetween>
          </Form>
        </form>
      )}
    </SpaceBetween>
  );
};
